import { forwardRef } from "react";

import { observer } from "mobx-react-lite";

import { useNodeThemeColors } from "@/components/expression/theme";
import { useSymbolPushTailwindClassName } from "@/components/expression/utils/layout/useSymbolPushTailwindClassName";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { twMerge } from "@/utils/twMerge";

import type { Text } from "@/components/expression/utils/NodeStyleProvider";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeSize } from "@corca/data";
import type { ComponentProps, ForwardedRef, PropsWithChildren } from "react";

const SIZE_CLASSES: Record<NodeSize, string> = {
  sm: "text-node-sm",
  xs: "text-node-xs",
  xxs: "text-node-xxs",
  xxxs: "text-node-xxxs",
};

const INDENT_CLASSES: Record<NodeSize, string> = {
  sm: "tracking-node-sm indent-node-sm",
  xs: "tracking-node-xs indent-node-xs",
  xxs: "tracking-node-xxs indent-node-xxs",
  xxxs: "tracking-node-xxxs indent-node-xxxs",
};

const TYPE_CLASSES: Record<Text, string> = {
  default: "text-node-text",
  parameter: "text-function-parameter",
  primary: "text-node-text-primary",
  unit: "text-gray-500 dark:text-gray-400",
};

const OVERWRITE_TEXT_TYPES: Text[] = ["unit"];

type Props = {
  indent?: boolean;
  node?: NodeModel;
  selectionVisible?: boolean;
  isHighlighted?: boolean;
  highlightClassName?: string;
  /** Not part of a MathML annotation, so screen readers read it. */
  isReadable?: boolean;
};

const NodeValue = forwardRef(
  (
    {
      className,
      indent = true,
      children,
      node,
      isHighlighted,
      highlightClassName,
      isReadable = false,
      ...props
    }: PropsWithChildren<Props & ComponentProps<"div">>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const textClassNames = useNodeTextClassNames({ node, indent });

    return (
      <div
        ref={ref}
        // hide from screenreaders to avoid reading after mathml annotation
        // hiding it at a wrapper level also hides inputs (in Firefox)
        aria-hidden={isReadable ? undefined : true}
        className={twMerge("relative", textClassNames, className)}
        {...props}
      >
        {children}
        {isHighlighted && (
          <div
            className={twMerge(
              "absolute left-0 bottom-0 right-0 h-[1px] border-b border-edit-border",
              highlightClassName,
            )}
          />
        )}
      </div>
    );
  },
);

const NodeInput = forwardRef(
  (
    {
      className,
      indent = true,
      selectionVisible = true,
      ...props
    }: Props & ComponentProps<"input">,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const textClassNames = useNodeTextClassNames({ indent, selectionVisible });

    return <input ref={ref} className={twMerge(textClassNames, className)} {...props} />;
  },
);

function useNodeTextClassNames({ node, indent, selectionVisible }: Props) {
  const { size, text, view } = useNodeStyle();
  const theme = useNodeThemeColors(node);
  const nodeValue = node && "value" in node ? node.value : "";
  const symbolPushClassName = useSymbolPushTailwindClassName(nodeValue);

  return twMerge(
    "font-normal print:no-underline",
    !selectionVisible && "selection:bg-transparent",
    SIZE_CLASSES[size],
    indent && INDENT_CLASSES[size],
    symbolPushClassName?.negative,

    theme?.symbol && `text-${theme.symbol.light} dark:text-${theme.symbol.dark}`,
    (!theme?.symbol || OVERWRITE_TEXT_TYPES.includes(text)) && TYPE_CLASSES[text],
    '[[data-change-request-part="before"]_[data-math]_&]:text-red-950 dark:[[data-change-request-part="before"]_[data-math]_&]:text-red-100',

    view === "text" && "normal-case",
  );
}

const ObservableNodeValue = observer(NodeValue);

const ObservableNodeInput = observer(NodeInput);

export { ObservableNodeValue as NodeValue, ObservableNodeInput as NodeInput };
